import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import useDebounce from "../hooks/useDebounce";

function rolesToIds(roles) {
  return Array.isArray(roles) ? roles.map((r) => r.id).filter(Boolean) : [];
}

function normalizeState(s) {
  return s === "allow" || s === "deny" || s === "inherit" ? s : "inherit";
}

function buildErrorMessage(e) {
  if (!e) return "Неизвестная ошибка";
  if (typeof e === "string") return e;
  if (e?.message) return e.message;
  return "Ошибка";
}

function toNullableInt(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const dq = useDebounce(q, 350);

  const [roles, setRoles] = useState([]);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);

  const [loadingList, setLoadingList] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  const [branches, setBranches] = useState([]); // City[] (филиалы)

  const [createMode, setCreateMode] = useState(false);

  // permissions состояния (inherit/allow/deny)
  const [allPerms, setAllPerms] = useState([]); // [{id, code, state}]
  const initialPermsRef = useRef([]); // снапшот чтобы понять что поменялось

  // статус вместо alert()
  const [notice, setNotice] = useState(null); // {type:"ok"|"err", text:string}
  const noticeTimerRef = useRef(null);
  const setNoticeSafe = (payload) => {
    setNotice(payload);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 3500);
  };

  // формы
  const [form, setForm] = useState({
    login: "",
    password: "",
    name: "",
    lastname: "",
    fathername: "",
    branch_id: "",
    is_global_access: false,
    department: "",
    position: "",
    role_ids: [],
  });

  const dirtyRef = useRef(false);

  const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
  const pages = useMemo(() => Math.max(1, Math.ceil(count / limit)), [count, limit]);

  const resetFormFromUser = (u) => {
    const role_ids = rolesToIds(u?.roles);
    setForm({
      login: u?.login || "",
      password: "",
      name: u?.name || "",
      lastname: u?.lastname || "",
      fathername: u?.fathername || "",
      branch_id: u?.branch_id ?? "",
      is_global_access: !!u?.is_global_access,
      department: u?.department || "",
      position: u?.position || "",
      role_ids,
    });
    dirtyRef.current = false;
  };

  // --- API helpers ---
  const loadPerms = async (userId) => {
    const res = await apiFetch(`/api/admin/users/${userId}/permissions`);
    if (!res.ok) {
      let msg = "Не удалось загрузить исключения прав";
      const t = await res.json().catch(() => ({}));
      if (t?.message) msg = t.message;
      throw new Error(msg);
    }
    const data = await res.json().catch(() => ({}));
    const perms = Array.isArray(data.permissions) ? data.permissions : [];
    const normalized = perms.map((p) => ({
      id: p.id,
      code: p.code,
      state: normalizeState(p.state),
    }));
    setAllPerms(normalized);
    initialPermsRef.current = normalized;
  };

  const setPermState = (permId, state) => {
    dirtyRef.current = true;
    setAllPerms((prev) => prev.map((p) => (p.id === permId ? { ...p, state } : p)));
  };

  // загрузить роли
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/admin/roles");
        if (!res.ok) throw new Error("roles fetch failed");
        const data = await res.json();
        setRoles(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setNoticeSafe({ type: "err", text: "Не удалось загрузить роли" });
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/city");
        if (!res.ok) throw new Error("branches fetch failed");
        const data = await res.json();
        setBranches(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setNoticeSafe({ type: "err", text: "Не удалось загрузить филиалы" });
      }
    })();
  }, []);

  // загрузить список
  useEffect(() => {
    (async () => {
      setLoadingList(true);
      try {
        const params = new URLSearchParams();
        if (dq) params.set("q", dq);
        params.set("limit", String(limit));
        params.set("offset", String(offset));

        const res = await apiFetch(`/api/admin/users?${params.toString()}`);
        if (!res.ok) throw new Error("list fetch failed");
        const data = await res.json();

        setItems(data.items || []);
        setCount(Number(data.count) || 0);
      } catch (e) {
        console.error(e);
        setNoticeSafe({ type: "err", text: "Не удалось загрузить пользователей" });
      } finally {
        setLoadingList(false);
      }
    })();
  }, [dq, limit, offset]);

  // загрузить выбранного
  const loadUser = async (id) => {
    setLoadingUser(true);
    try {
      const res = await apiFetch(`/api/admin/users/${id}`);
      if (!res.ok) {
        const t = await res.json().catch(() => ({}));
        throw new Error(t.message || "Не удалось загрузить пользователя");
      }
      const data = await res.json();
      setSelected(data);
      resetFormFromUser(data);

      // важно: сразу подгружаем perms, чтобы не надо было обновлять страницу
      await loadPerms(id);
    } catch (e) {
      console.error(e);
      setNoticeSafe({ type: "err", text: buildErrorMessage(e) });
    } finally {
      setLoadingUser(false);
    }
  };

  const onSelect = async (id) => {
    if (dirtyRef.current) {
      const ok = window.confirm("Есть несохранённые изменения. Переключиться и потерять их?");
      if (!ok) return;
    }

    setNotice(null);
    setCreateMode(false);
    setSelectedId(id);
    await loadUser(id);
  };

  const onCreateNew = () => {
    if (dirtyRef.current) {
      const ok = window.confirm("Есть несохранённые изменения. Перейти к созданию нового и потерять их?");
      if (!ok) return;
    }
    setNotice(null);
    setSelectedId(null);
    setSelected(null);
    setCreateMode(true);

    setForm({
      login: "",
      password: "",
      name: "",
      lastname: "",
      fathername: "",
      branch_id: "",
      is_global_access: false,
      department: "",
      position: "",
      role_ids: [],
    });

    dirtyRef.current = false;

    // perms в режиме создания не нужны
    setAllPerms([]);
    initialPermsRef.current = [];
  };

  const setField = (k, v) => {
    dirtyRef.current = true;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const toggleRole = (id) => {
    dirtyRef.current = true;
    setForm((p) => {
      const has = p.role_ids.includes(id);
      return {
        ...p,
        role_ids: has ? p.role_ids.filter((x) => x !== id) : [...p.role_ids, id],
      };
    });
  };

  const saveExisting = async () => {
    if (!selectedId) return;
    setSaving(true);
    setNotice(null);

    const branchIdNum = toNullableInt(form.branch_id);

    try {
      // 1) PATCH user fields
      const patchBody = {
        login: form.login,
        name: form.name,
        lastname: form.lastname,
        fathername: form.fathername,
        branch_id: Number.isFinite(branchIdNum) ? branchIdNum : null,
        is_global_access: !!form.is_global_access,
        department: form.department,
        position: form.position,
      };
      if (form.password && form.password.trim()) patchBody.password = form.password;

      const r1 = await apiFetch(`/api/admin/users/${selectedId}`, {
        method: "PATCH",
        body: patchBody,
      });
      if (!r1.ok) {
        const t = await r1.json().catch(() => ({}));
        throw new Error(t.message || "Не удалось сохранить пользователя");
      }

      // 2) PUT roles
      const r2 = await apiFetch(`/api/admin/users/${selectedId}/roles`, {
        method: "PUT",
        body: { role_ids: form.role_ids },
      });
      if (!r2.ok) {
        const t = await r2.json().catch(() => ({}));
        throw new Error(t.message || "Не удалось сохранить роли");
      }

      // 3) PUT user_permissions overrides (только изменения)
      const initialMap = new Map((initialPermsRef.current || []).map((p) => [p.id, p.state]));
      const currentMap = new Map((allPerms || []).map((p) => [p.id, p.state]));

      const changed = [];
      for (const [pid, curState] of currentMap.entries()) {
        const oldState = initialMap.get(pid) || "inherit";
        if (curState !== oldState) {
          changed.push({
            permission_id: pid,
            effect: curState === "inherit" ? null : curState, // allow/deny/null
          });
        }
      }

      if (changed.length > 0) {
        const r3 = await apiFetch(`/api/admin/users/${selectedId}/permissions`, {
          method: "PUT",
          body: { items: changed },
        });
        if (!r3.ok) {
          const t = await r3.json().catch(() => ({}));
          throw new Error(t.message || "Не удалось сохранить исключения прав");
        }

        const data3 = await r3.json().catch(() => ({}));
        const normalized = (data3?.permissions || []).map((p) => ({
          id: p.id,
          code: p.code,
          state: normalizeState(p.state),
        }));
        setAllPerms(normalized);
        initialPermsRef.current = normalized;
      }

      const updated = await r2.json();
      setSelected(updated);
      resetFormFromUser(updated);

      // обновим список (чтобы роли в левой колонке тоже обновились)
      setOffset((x) => x);

      setNoticeSafe({ type: "ok", text: "Сохранено" });
      dirtyRef.current = false;
    } catch (e) {
      console.error(e);
      setNoticeSafe({ type: "err", text: buildErrorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const createUser = async () => {
    if (!form.login.trim()) return setNoticeSafe({ type: "err", text: "Login обязателен" });
    if (!form.password.trim()) return setNoticeSafe({ type: "err", text: "Password обязателен" });

    setSaving(true);
    setNotice(null);

    const branchIdNum = toNullableInt(form.branch_id);

    try {
      const r = await apiFetch(`/api/admin/users`, {
        method: "POST",
        body: {
          login: form.login,
          password: form.password,
          name: form.name,
          lastname: form.lastname,
          fathername: form.fathername,
          branch_id: Number.isFinite(branchIdNum) ? branchIdNum : null,
          is_global_access: !!form.is_global_access,
          department: form.department,
          position: form.position,
          role_ids: form.role_ids,
        },
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "Не удалось создать пользователя");

      // после создания — сразу переходим в режим редактирования + грузим perms
      setCreateMode(false);
      setSelectedId(data.id);
      setSelected(data);
      resetFormFromUser(data);

      // важно: чтобы блок исключений появился сразу без обновления страницы
      await loadPerms(data.id);

      // перезагрузим список с первой страницы
      setOffset(0);

      setNoticeSafe({ type: "ok", text: "Пользователь создан" });
      dirtyRef.current = false;
    } catch (e) {
      console.error(e);
      setNoticeSafe({ type: "err", text: buildErrorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="container">
        <div className="admin-users">
          <div className="admin-users__left">
            <div className="admin-users__toolbar">
              <input
                className="admin-users__search"
                placeholder="Поиск: логин, ФИО, филиал, отдел, должность..."
                value={q}
                onChange={(e) => {
                  setOffset(0);
                  setQ(e.target.value);
                }}
              />
              <button className="admin-users__btn admin-users__btn--primary" onClick={onCreateNew}>
                + Новый
              </button>
            </div>

            <div className="admin-users__list">
              {loadingList ? (
                <div className="admin-users__muted">Загрузка...</div>
              ) : items.length === 0 ? (
                <div className="admin-users__muted">Нет пользователей</div>
              ) : (
                items.map((u) => {
                  const active = u.id === selectedId && !createMode;
                  return (
                    <button
                      key={u.id}
                      className={`admin-users__row ${active ? "is-active" : ""}`}
                      onClick={() => onSelect(u.id)}
                      type="button"
                    >
                      <div className="admin-users__rowTop">
                        <div className="admin-users__login">{u.login}</div>
                        <div className="admin-users__id">#{u.id}</div>
                      </div>

                      <div className="admin-users__fio">
                        {[u.lastname, u.name, u.fathername].filter(Boolean).join(" ") || "—"}
                      </div>

                      <div className="admin-users__meta">
                        <span>{u.branch_name || "—"}</span>
                        <span>·</span>
                        <span>{u.department || "—"}</span>
                      </div>

                      <div className="admin-users__roles">
                        {(u.roles || []).slice(0, 3).map((r) => (
                          <span key={r.id} className="admin-users__badge">
                            {r.code}
                          </span>
                        ))}
                        {(u.roles || []).length > 3 ? (
                          <span className="admin-users__badge admin-users__badge--more">
                            +{(u.roles || []).length - 3}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <div className="admin-users__pager">
              <button className="admin-users__btn" disabled={page <= 1} onClick={() => setOffset((o) => Math.max(0, o - limit))}>
                ←
              </button>
              <div className="admin-users__muted">
                Стр. {page} / {pages} · Всего: {count}
              </div>
              <button className="admin-users__btn" disabled={page >= pages} onClick={() => setOffset((o) => o + limit)}>
                →
              </button>
            </div>
          </div>

          <div className="admin-users__right">
            <div className="admin-users__panel">
              <div className="admin-users__panelHeader">
                <div className="admin-users__title">
                  {createMode ? "Создать пользователя" : selected ? `Пользователь #${selected.id}` : "Выберите пользователя"}
                </div>
                {!createMode && selected ? <div className="admin-users__muted">{selected.login}</div> : null}
              </div>

              {/* Статус вместо alert */}
              {/* {notice ? (
                <div
                  className={`admin-users__notice ${notice.type === "ok" ? "is-ok" : "is-err"}`}
                  role="status"
                  aria-live="polite"
                >
                  <span className="admin-users__noticeIcon">{notice.type === "ok" ? "✅" : "❌"}</span>
                  <span className="admin-users__noticeText">{notice.text}</span>
                </div>
              ) : null} */}

              {loadingUser ? (
                <div className="admin-users__muted">Загрузка...</div>
              ) : createMode || selected ? (
                <>
                  <div className="admin-users__grid">
                    <label className="admin-users__field">
                      <div className="admin-users__label">Login *</div>
                      <input
                        value={form.login}
                        onChange={(e) => setField("login", e.target.value)}
                        className="admin-users__input"
                        autoComplete="off"
                      />
                    </label>

                    <label className="admin-users__field">
                      <div className="admin-users__label">Password {createMode ? "*" : "(если нужно сменить)"}</div>
                      <input
                        value={form.password}
                        onChange={(e) => setField("password", e.target.value)}
                        className="admin-users__input"
                        type="password"
                        autoComplete="new-password"
                      />
                    </label>

                    <label className="admin-users__field">
                      <div className="admin-users__label">Фамилия</div>
                      <input value={form.lastname} onChange={(e) => setField("lastname", e.target.value)} className="admin-users__input" />
                    </label>

                    <label className="admin-users__field">
                      <div className="admin-users__label">Имя</div>
                      <input value={form.name} onChange={(e) => setField("name", e.target.value)} className="admin-users__input" />
                    </label>

                    <label className="admin-users__field">
                      <div className="admin-users__label">Отчество</div>
                      <input
                        value={form.fathername}
                        onChange={(e) => setField("fathername", e.target.value)}
                        className="admin-users__input"
                      />
                    </label>

                    {/* <label className="admin-users__field">
                      <div className="admin-users__label">Филиал</div>
                      <input value={form.branch} onChange={(e) => setField("branch", e.target.value)} className="admin-users__input" />
                    </label> */}

                    <label className="admin-users__field">
                      <div className="admin-users__label">Филиал</div>

                      <select
                        className="admin-users__input"
                        value={form.branch_id === null || form.branch_id === undefined ? "" : String(form.branch_id)}
                        onChange={(e) => setField("branch_id", e.target.value === "" ? "" : Number(e.target.value))}
                      >
                        <option value="">— Не выбран —</option>
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name || b.title || b.slug || `#${b.id}`}
                          </option>
                        ))}
                      </select>

                      {selected?.branch_name ? (
                        <div className="admin-users__muted" style={{ marginTop: 6 }}>
                          Сейчас: {selected.branch_name}
                        </div>
                      ) : null}
                    </label>

                    <label className="admin-users__field">
                      <div className="admin-users__label">Отдел</div>
                      <input
                        value={form.department}
                        onChange={(e) => setField("department", e.target.value)}
                        className="admin-users__input"
                      />
                    </label>

                    <label className="admin-users__field">
                      <div className="admin-users__label">Должность</div>
                      <input
                        value={form.position}
                        onChange={(e) => setField("position", e.target.value)}
                        className="admin-users__input"
                      />
                    </label>

                    <label className="admin-users__field" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={!!form.is_global_access}
                        onChange={(e) => setField("is_global_access", e.target.checked)}
                      />
                      <div>
                        <div className="admin-users__label" style={{ margin: 0 }}>Глобальный доступ</div>
                        {/* <div className="admin-users__muted" style={{ marginTop: 2 }}>
                          Если включено — филиальная привязка не ограничивает
                        </div> */}
                      </div>
                    </label>
                  </div>

                  <div className="admin-users__section">
                    <div className="admin-users__sectionTitle">Роли</div>
                    <div className="admin-users__rolesGrid">
                      {roles.map((r) => (
                        <label key={r.id} className="admin-users__roleItem">
                          <input type="checkbox" checked={form.role_ids.includes(r.id)} onChange={() => toggleRole(r.id)} />
                          {/* <span className="admin-users__roleCode">{r.code}</span> */}
                          <span className="admin-users__muted">{r.name || ""}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Исключения прав: только после создания/выбора пользователя */}
                  {!createMode && selected ? (
                    <div className="admin-users__section">
                      <div className="admin-users__sectionTitle">Исключения прав</div>
                      {/* <div className="admin-users__muted" style={{ marginTop: 6 }}>
                        По умолчанию = по ролям · Разрешить = принудительно allow · Запретить = принудительно deny
                      </div> */}

                      <div className="admin-users__permsList">
                        {allPerms.length === 0 ? (
                          <div className="admin-users__muted" style={{ padding: "10px 0" }}>
                            Нет permissions
                          </div>
                        ) : (
                          allPerms.map((p) => (
                            <div key={p.id} className="admin-users__permRow">
                              <div className="admin-users__permCode">{p.code}</div>

                              <div className="admin-users__permActions">
                                <button
                                  type="button"
                                  className={`admin-users__permBtn ${p.state === "inherit" ? "is-active" : ""}`}
                                  onClick={() => setPermState(p.id, "inherit")}
                                >
                                  По умолчанию
                                </button>
                                <button
                                  type="button"
                                  className={`admin-users__permBtn admin-users__permBtn--allow ${
                                    p.state === "allow" ? "is-active" : ""
                                  }`}
                                  onClick={() => setPermState(p.id, "allow")}
                                >
                                  Разрешить
                                </button>
                                <button
                                  type="button"
                                  className={`admin-users__permBtn admin-users__permBtn--deny ${
                                    p.state === "deny" ? "is-active" : ""
                                  }`}
                                  onClick={() => setPermState(p.id, "deny")}
                                >
                                  Запретить
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ) : null}

                  <div className="admin-users__actions">
                    {notice ? (
                      <div
                        className={`admin-users__notice ${notice.type === "ok" ? "is-ok" : "is-err"}`}
                        role="status"
                        aria-live="polite"
                      >
                        <span className="admin-users__noticeIcon">{notice.type === "ok" ? "✅" : "❌"}</span>
                        <span className="admin-users__noticeText">{notice.text}</span>
                      </div>
                    ) : null}

                    {createMode ? (
                      <button className="admin-users__btn admin-users__btn--primary" disabled={saving} onClick={createUser}>
                        {saving ? "Создание..." : "Создать"}
                      </button>
                    ) : (
                      <button className="admin-users__btn admin-users__btn--primary" disabled={saving || !selectedId} onClick={saveExisting}>
                        {saving ? "Сохранение..." : "Сохранить"}
                      </button>
                    )}

                    <button
                      className="admin-users__btn"
                      disabled={saving}
                      onClick={() => {
                        setNotice(null);
                        if (createMode) onCreateNew();
                        else if (selected) {
                          resetFormFromUser(selected);
                          // откатим perms к исходному снимку
                          const snap = initialPermsRef.current || [];
                          setAllPerms(snap.map((p) => ({ ...p })));
                          dirtyRef.current = false;
                          setNoticeSafe({ type: "ok", text: "Изменения сброшены" });
                        }
                      }}
                    >
                      Сбросить
                    </button>
                  </div>
                </>
              ) : (
                <div className="admin-users__muted">Выбери пользователя слева или нажми “Новый”</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
